'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Modal } from '@/app/components/ui/Modal';
import { Button } from '@/app/components/ui/Button';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { Checkbox } from '@/app/components/ui/Checkbox';
import { useWillStore } from '@/lib/store';
import { Loader2, CheckCircle } from 'lucide-react';
import { SocialSignInButtons } from './auth/SocialSignInButtons';
import { createClient } from '@/lib/supabase/client';
import { requestMagicLink } from '@/lib/auth/magic-link';
import { AlertCircle, CheckCircle as CheckCircleIcon } from 'lucide-react';

interface SaveProgressModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (email?: string) => void;
    onSignIn?: () => void;
    title?: string;
}

export const SaveProgressModal = ({
    isOpen,
    onClose,
    onSuccess,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSignIn: _onSignIn,
    title,
    mode = 'payment'
}: SaveProgressModalProps & { mode?: 'save' | 'payment' }) => {
    const router = useRouter();
    const { data, willId, reset, setAuth } = useWillStore();
    const [email, setEmail] = useState(data.personalDetails?.email || '');
    const [marketingOptOut, setMarketingOptOut] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [otpCode, setOtpCode] = useState('');
    const [canResend, setCanResend] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(60);
    const [isResending, setIsResending] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [lastSentAt, setLastSentAt] = useState<number | null>(null);

    const isPaymentMode = mode === 'payment';

    const isValidEmail = (emailToCheck: string): boolean => {
        const value = (emailToCheck || '').trim();
        // Basic but strict: local@domain.tld with 2+ char TLD, no spaces
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return value.length >= 6 && value.length <= 254 && emailRegex.test(value);
    };

    useEffect(() => {
        if (isOpen) {
            setStep('email');
            setOtpCode('');
            setError(null);
            setCanResend(false);
            setResendCountdown(60);
            setIsResending(false);
            setIsSending(false);
            setLastSentAt(null);
        }
    }, [isOpen]);

    // Handle redirect on success for save mode
    React.useEffect(() => {
        if (isSuccess && mode === 'save') {
            const timer = setTimeout(() => {
                router.push('/');
                onClose();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [isSuccess, mode, router, onClose]);

    // Countdown for resend
    useEffect(() => {
        if (step === 'otp' && !canResend) {
            const timer = setInterval(() => {
                setResendCountdown((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        clearInterval(timer);
                        return 60;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [step, canResend]);

    const buildRedirectUrl = (guestToken?: string) => {
        if (typeof window === 'undefined') return undefined;
        const nextPath = mode === 'save' ? '/?saved=1' : '/write-a-will-online';
        const params = new URLSearchParams();
        params.set('next', nextPath);
        if (guestToken) {
            params.set('guest_token', guestToken);
        }
        return `${window.location.origin}/auth/callback?${params.toString()}`;
    };

    const sendMagicLink = async (emailAddress: string, allowSignup = true, guestToken?: string) => {
        if (!isValidEmail(emailAddress)) {
            setError('Please enter a valid email address');
            return false;
        }

        if (lastSentAt && Date.now() - lastSentAt < 55000) {
            return true;
        }

        const redirectTo = buildRedirectUrl(guestToken);
        const result = await requestMagicLink({
            email: emailAddress,
            allowCreate: allowSignup,
            redirectTo,
            context: allowSignup ? 'signup' : 'signin',
        });

        if (!result.ok) {
            if (result.code === 'rate_limited') {
                setLastSentAt(Date.now());
                return true;
            }
            setError(result.message);
            return false;
        }

        setStep('otp');
        setCanResend(false);
        setResendCountdown(60);
        setLastSentAt(Date.now());
        return true;
    };

    const handleResend = async () => {
        if (!email) return;
        setIsResending(true);
        setError(null);

        await sendMagicLink(email, isPaymentMode);

        setIsResending(false);
    };

    const handleVerifyOtp = async () => {
        if (!email || otpCode.length < 6) return;
        setIsSending(true);
        setError(null);

        try {
            const supabase = createClient();
            const { data: { session }, error: verifyError } = await supabase.auth.verifyOtp({
                email,
                token: otpCode,
                type: 'email',
            });

            if (verifyError) throw verifyError;

            if (isPaymentMode && session?.user) {
                const user = session.user;

                // Ensure we have a will for this user before marking as authenticated
                const supabaseClient = createClient();
                let willId = useWillStore.getState().willId || '';
                const fullName = data.personalDetails?.fullName?.trim() || 'User';

                if (!willId) {
                    const { data: existingWills } = await supabaseClient
                        .from('wills')
                        .select('id')
                        .eq('user_id', user.id)
                        .order('updated_at', { ascending: false })
                        .limit(1);

                    if (existingWills && existingWills.length > 0) {
                        willId = existingWills[0].id;
                    }
                }

                if (!willId) {
                    const { data: newWill, error: createError } = await supabaseClient
                        .from('wills')
                        .insert({
                            user_id: user.id,
                            email: user.email || email,
                            full_name: fullName,
                            status: 'draft',
                            will_data: {},
                        })
                        .select('id')
                        .single();

                    if (createError) {
                        throw createError;
                    }

                    willId = newWill?.id || '';
                }

                if (!willId) {
                    throw new Error('Could not prepare a will for payment.');
                }

                setAuth(user.id, user.email || email, willId);

                if (onSuccess) {
                    onSuccess(email);
                } else {
                    onClose();
                }
            } else {
                // Save flow or fallback: close modal and let session listener handle the rest
                onClose();
            }
        } catch (err: unknown) {
            console.error('Error verifying code:', err);
            const errorMessage = err instanceof Error ? err.message : 'Invalid code. Please try again.';
            setError(errorMessage);
        } finally {
            setIsSending(false);
        }
    };

    const handleSaveOrSend = async () => {
        if (!email) return;

        // Payment flow uses OTP first
        if (isPaymentMode) {
            if (step === 'otp') {
                await handleVerifyOtp();
                return;
            }

            setError(null);
            setIsSending(true);
            await sendMagicLink(email, true);
            setIsSending(false);
            return;
        }

        setLoading(true);
        setError(null);

        // Update the store's email if not set
        if (!data.personalDetails?.email) {
            useWillStore.getState().setData({
                personalDetails: {
                    ...data.personalDetails,
                    email: email
                }
            });
        }

        if (mode === 'save') {
            try {
                const response = await fetch('/api/save-progress', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...data,
                        willId,
                        email
                    })
                });

                const result = await response.json();

                if (response.status === 202 && result.guestToken) {
                    // Guest flow: stage save and immediately prompt verification via magic link
                    setError(null);
                    await sendMagicLink(email, true, result.guestToken);
                    setLoading(false);
                    return;
                }

                if (!response.ok) {
                    throw new Error(result.error || 'Failed to save progress');
                }

                if (onSuccess) {
                    onSuccess();
                } else {
                    // Show success state then redirect (handled by useEffect)
                    setIsSuccess(true);
                }
            } catch (err: unknown) {
                const errorMessage = err instanceof Error ? err.message : 'An error occurred';
                // If account exists, auto-send OTP and show input
                if (errorMessage.includes('Account already exists') || errorMessage.includes('sign in')) {
                    setError('Account exists. We sent you a code to verify it\'s you.');
                    await sendMagicLink(email, false);
                } else {
                    setError(errorMessage || 'An error occurred while saving.');
                }
            } finally {
                setLoading(false);
            }
        }
    };

    const resetModal = () => {
        setError(null);
        setIsSuccess(false);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={resetModal} title={title || (mode === 'save' ? "Save & Exit" : "Setup your Account")}>
            {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground text-center">
                        Your Will draft has been saved
                    </h3>
                    <p className="text-muted-foreground text-center">
                        Redirecting you to the homepage...
                    </p>
                </div>
            ) : (
                <div className="space-y-5">
                    {step === 'otp' ? (
                        <div className="space-y-6">
                            <div className="text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
                                    <CheckCircleIcon className="w-6 h-6 text-green-600" />
                                </div>
                                <h3 className="text-lg font-bold text-foreground mb-1">
                                    Check your email
                                </h3>
                                <p className="text-muted-foreground text-sm mb-1">
                                    We&apos;ve sent a 6-digit code to
                                </p>
                                <p className="font-medium text-foreground text-sm">
                                    {email}
                                </p>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Enter the one-time passcode to continue.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="otp-code">Verification Code</Label>
                                <input
                                    id="otp-code"
                                    type="text"
                                    placeholder="123456"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    maxLength={6}
                                    className="w-full text-center text-2xl tracking-[0.5em] font-mono h-14 rounded-lg border border-foreground/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    autoFocus
                                />
                                <p className="text-xs text-muted-foreground text-center">
                                    Sent to {email}
                                </p>
                            </div>

                            {error && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2 text-left">
                                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <div className="space-y-3">
                                <Button
                                    onClick={handleVerifyOtp}
                                    disabled={isSending || otpCode.length < 6}
                                    className="w-full h-11 text-base"
                                >
                                    {isSending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Verifying...
                                        </>
                                    ) : (
                                        isPaymentMode ? "Verify & Continue" : "Verify & Save"
                                    )}
                                </Button>

                                <div className="space-y-2 text-center">
                                    <p className="text-sm text-muted-foreground">
                                        Didn&apos;t receive the code?
                                    </p>
                                    {canResend ? (
                                        <Button
                                            variant="outline"
                                            onClick={handleResend}
                                            disabled={isResending}
                                            className="w-full"
                                        >
                                            {isResending ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                'Resend code'
                                            )}
                                        </Button>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            Resend in {resendCountdown}s
                                        </p>
                                    )}
                                    <button
                                        onClick={() => {
                                            setStep('email');
                                            setOtpCode('');
                                            setError(null);
                                        }}
                                        className="w-full text-center text-sm text-primary hover:underline"
                                    >
                                        Use a different email
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <p className="text-muted-foreground text-sm">
                                {mode === 'save'
                                    ? "Enter your email to save your progress. You can log back in any time and continue."
                                    : "We'll save your Will securely so you can access it anytime."}
                            </p>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="save-email">Email</Label>
                                    <Input
                                        id="save-email"
                                        type="email"
                                        placeholder="name@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-gray-200" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-white px-2 text-muted-foreground">
                                            Or
                                        </span>
                                    </div>
                                </div>
                                <SocialSignInButtons
                                    redirectTo={typeof window !== 'undefined' ? `${window.location.origin}/auth/callback?next=${encodeURIComponent('/write-a-will-online?auto_payment=true')}` : undefined}
                                />
                                <div className="flex items-center gap-3">
                                    <Checkbox
                                        checked={marketingOptOut}
                                        onCheckedChange={(checked) => setMarketingOptOut(!!checked)}
                                        className="border-none p-0 hover:border-none"
                                    />
                                    <span className="text-sm text-muted-foreground leading-relaxed">
                                        We may send you marketing emails from time to time. To opt out, tick this box.
                                    </span>
                                </div>
                                {error && (
                                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                        <span>{error}</span>
                                    </div>
                                )}
                            </div>

                            <Button
                                onClick={handleSaveOrSend}
                                disabled={isSending || loading || !email}
                                className="w-full h-11 text-base"
                            >
                                {(isSending || loading) ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {isPaymentMode ? 'Sending...' : 'Processing...'}
                                    </>
                                ) : (
                                    mode === 'save' ? "Save & Exit" : "Continue to Payment"
                                )}
                            </Button>
                        </div>
                    )}

                    {mode === 'save' && (
                        <button
                            type="button"
                            onClick={() => {
                                reset();
                                onClose();
                                router.push('/');
                            }}
                            className="w-full text-sm text-muted-foreground hover:text-destructive transition-colors"
                        >
                            Leave without saving
                        </button>
                    )}

                </div>
            )}
        </Modal>
    );
};
